import { DragEvent } from "react";

import { PlayingSurfaceCourtView } from "../../../redux/slices";
import { OutOfBounds } from "../playing-surface";

export interface GetSurfacePositionProps {
  svgElement: SVGSVGElement;
  offsetX: number;
  offsetY: number;
  surfaceContainerElement: HTMLDivElement;
  event: DragEvent<HTMLDivElement> | MouseEvent;
  adjustXUnits?: number;
  adjustYUnits?: number;
  surfaceWidthUnits: number;
  surfaceHeightUnits: number;
  orientation: 0 | 90 | 180 | 270;
}

export const getSurfacePosition = ({
  event,
  offsetX,
  offsetY,
  svgElement,
  surfaceContainerElement,
  adjustXUnits = 0,
  adjustYUnits = 0,
  surfaceWidthUnits,
  surfaceHeightUnits,
  orientation,
}: GetSurfacePositionProps) => {
  const isCourtContainer = event.currentTarget === event.target;
  const inCourtContainer = event.currentTarget === event.target;

  const { x: surfaceContainerX, y: surfaceContainerY } = surfaceContainerElement.getBoundingClientRect();
  const { x: svgX, y: svgY, width: svgWidth, height: svgHeight } = svgElement.getBoundingClientRect();

  // number of pixels per surface unit
  const surfaceWidthUnitPxs =
    orientation === 0 || orientation === 180 ? svgWidth / surfaceWidthUnits : svgHeight / surfaceWidthUnits;
  const surfaceHeightUnitPxs =
    orientation === 0 || orientation === 180 ? svgHeight / surfaceHeightUnits : svgWidth / surfaceHeightUnits;

  // calculate the x and y pixel gap between the svg element and the surface container element
  const xGap = svgX - surfaceContainerX;
  const yGap = svgY - surfaceContainerY;

  const { relativeXPos, relativeYPos } = (() => {
    switch (orientation) {
      case 0: {
        // get the x and y position as pixels relative to the svg origin
        // if hovering outside the svg, the x and y offset will be relative to the surface container element.
        // Will need to adjust for this by subtracting the gap
        return {
          relativeXPos: offsetX - (inCourtContainer ? xGap : 0),
          relativeYPos: offsetY - (inCourtContainer ? yGap : 0),
        };
      }

      case 90: {
        // When hovering inside the svg, xOffset and yOffset are relative to the svg origin and take rotation into
        // account. When hovering outside the svg, xOffset and yOffset are relative to the surface container element
        // and need to use yOffset to calculate the relativeXPos and vice versa
        return {
          relativeXPos: isCourtContainer ? offsetY - yGap : offsetX,
          relativeYPos: isCourtContainer ? svgWidth + xGap - offsetX : offsetY,
        };
      }

      case 180: {
        return {
          relativeXPos: isCourtContainer ? svgWidth + xGap - offsetX : offsetX,
          relativeYPos: isCourtContainer ? svgHeight + yGap - offsetY : offsetY,
        };
      }
      case 270: {
        return {
          relativeXPos: isCourtContainer ? svgHeight + yGap - offsetY : offsetX,
          relativeYPos: isCourtContainer ? offsetX - xGap : offsetY,
        };
      }
    }
  })();

  // divide the relative x and y pixels by the # of pixels in a unit to get the x and y unit
  const xUnit = relativeXPos / surfaceWidthUnitPxs + adjustXUnits;
  const yUnit = relativeYPos / surfaceHeightUnitPxs + adjustYUnits;

  return { x: xUnit, y: yUnit };
};

export const getSurfaceUnits = (
  outOfBounds?: OutOfBounds,
  view?: PlayingSurfaceCourtView
): { surfaceWidthUnits: number; surfaceHeightUnits: number } => {
  if (!view) return { surfaceWidthUnits: 100, surfaceHeightUnits: 100 };

  let width = view.width;
  let height = view.height;

  if (outOfBounds) {
    if (outOfBounds.left) width += 5;
    if (outOfBounds.right) width += 5;
    if (outOfBounds.top) height += 5;
    if (outOfBounds.bottom) height += 5;
  }

  return { surfaceWidthUnits: width, surfaceHeightUnits: height };
};
